// src/components/results/FlowLoopMetricBarChart.jsx
import { Bar } from "react-chartjs-2";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { fmtNumber, loopIndexFromKey } from "../../utils/loopsHelpers";
import { FLOW_METRIC_COLUMNS } from "../../utils/flowMetricColumns";
import { getPrimaryColour } from "../../utils/getPrimaryColour";

export function FlowLoopMetricBarChart({
  metrics = {},
  selectedMetric,
  onSelectMetric,
}) {
  const { t } = useTranslation();

  const loopKeys = useMemo(
    () =>
      Object.keys(metrics).sort(
        (a, b) => loopIndexFromKey(a) - loopIndexFromKey(b),
      ),
    [metrics],
  );

  // Dropdown order must match table columns
  const metricOptions = useMemo(() => {
    // only include columns that exist anywhere in metrics (optional but recommended)
    const available = new Set();
    for (const lk of loopKeys) {
      const row = metrics?.[lk];
      if (!row || typeof row !== "object") continue;
      Object.keys(row).forEach((k) => available.add(k));
    }
    return FLOW_METRIC_COLUMNS.filter((c) => available.has(c));
  }, [metrics, loopKeys]);

  // If you want the chart to own state, keep your local state.
  // But to ALSO highlight the table, it’s cleaner to lift selectedMetric up (see section 4).
  const [localSelected, setLocalSelected] = useState(() => "");

  const effectiveSelectedMetric = selectedMetric ?? localSelected;
  const setSelected = onSelectMetric ?? setLocalSelected;

  // keep selection valid as options load/change (hook-safe)
  useEffect(() => {
    if (!metricOptions.length) return;

    if (
      effectiveSelectedMetric &&
      metricOptions.includes(effectiveSelectedMetric)
    )
      return;

    const next = metricOptions[0];

    setSelected(next);
  }, [metricOptions, effectiveSelectedMetric, setSelected]);

  const labels = useMemo(
    () =>
      loopKeys.map((lk) => {
        const n = loopIndexFromKey(lk);
        return Number.isFinite(n) ? `Loop ${n}` : lk;
      }),
    [loopKeys],
  );

  const values = useMemo(() => {
    return loopKeys.map((lk) => {
      const v = metrics?.[lk]?.[effectiveSelectedMetric];
      return Number.isFinite(Number(v)) ? Number(v) : null;
    });
  }, [metrics, loopKeys, effectiveSelectedMetric]);

  if (!loopKeys.length || !metricOptions.length) return null;

  const isPercent = effectiveSelectedMetric === "Percent Change";
  const primary = getPrimaryColour({ format: "rgb", opacity: 0.5 });

  const data = {
    labels,
    datasets: [
      {
        label: effectiveSelectedMetric,
        data: values,
        borderWidth: 1,
        borderColor: primary,
        backgroundColor: primary,
      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (ctx) => {
            const v = ctx.parsed?.y;
            if (!Number.isFinite(v)) return "N/A";
            if (isPercent) return `${fmtNumber(v, 2)}%`;

            const dp = effectiveSelectedMetric.includes("Flow")
              ? 0
              : effectiveSelectedMetric.includes("Volume")
                ? 3
                : 3;

            return fmtNumber(v, dp);
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          maxRotation: 0,
          minRotation: 0,
          autoSkip: true,
        },
      },
      y: {
        title: {
          display: true,
          text: effectiveSelectedMetric,
        },
        ticks: {
          callback: (v) => (isPercent ? `${v}%` : v),
        },
      },
    },
  };

  return (
    <div className="w-full flex flex-col gap-2 justify-end">
      <p className="font-semibold">
        {t("results.flowLoops.metricBarChartTitle", {
          defaultValue: "Flow-Volume Loop Metrics",
        })}
      </p>

      <label className="text-sm text-greyscale-3">
        <select
          className="px-2 py-1 rounded-md border border-greyscale-1 text-sm bg-white focus:outline-primary"
          value={effectiveSelectedMetric}
          onChange={(e) => setSelected(e.target.value)}
        >
          {metricOptions.map((m) => (
            <option key={m} value={m}>
              {m}
            </option>
          ))}
        </select>
      </label>

      <div className="w-full h-56">
        <Bar data={data} options={options} />
      </div>
    </div>
  );
}
